import { type CSSProperties, type ReactNode } from 'react'
import { useSidebarResize } from '../hooks/useSidebarResize'

interface NewsLayoutProps {
  sidebar: ReactNode
  main: ReactNode
}

/**
 * Responsive two-pane layout.
 *
 * Below `lg` the panes stack and the sidebar scrolls with the page. From `lg` up the
 * sidebar becomes a full-height, independently scrolling column whose width the user
 * can drag.
 *
 * The width is published as a CSS custom property rather than an inline `width`.
 * Reading `window.innerWidth` during render (as this used to) never updated on resize
 * or rotation, beat every `lg:` class on specificity, and broke prerendering.
 */
export function NewsLayout({ sidebar, main }: NewsLayoutProps) {
  const { sidebarWidth, handlePointerDown, handleResizeKeyDown, minWidth, maxWidth } =
    useSidebarResize()

  return (
    <div
      className="flex min-h-screen flex-col lg:flex-row"
      style={{ '--sidebar-w': `${sidebarWidth}px` } as CSSProperties}
    >
      <aside
        aria-label="검색 설정"
        className="relative w-full border-b bg-card lg:sticky lg:top-0 lg:h-screen lg:w-[var(--sidebar-w)] lg:shrink-0 lg:overflow-y-auto lg:border-b-0 lg:border-r"
      >
        {sidebar}

        {/*
          Resize handle — pointer-driven so mouse, touch and pen share one path, and
          keyboard-operable, which the old mouse-only button was not.

          A focusable `separator` with aria-value* is the ARIA window-splitter
          pattern, and is a widget by spec. jsx-a11y still classifies `separator` as
          non-interactive, so these two rules are wrong here specifically.
        */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="사이드바 너비 조절"
          aria-valuenow={sidebarWidth}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onKeyDown={handleResizeKeyDown}
          className="absolute right-0 top-0 hidden h-full w-1.5 cursor-col-resize bg-transparent hover:bg-primary/20 focus-visible:bg-primary/40 focus-visible:outline-none active:bg-primary/40 lg:block"
          style={{ touchAction: 'none' }}
        />
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      </aside>

      {/* min-w-0 lets horizontally scrolling children (the keyword tabs) shrink
          instead of stretching this flex item past the viewport. */}
      <main aria-label="검색 결과" className="min-w-0 flex-1">
        {main}
      </main>
    </div>
  )
}
